import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { treatmentsService } from '../../../../services/treatments';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('TreatmentsInput');

type TreatmentsInputScreenRouteProp = RouteProp<RootStackParamList, 'TreatmentsInput'>;

// Helper: format phone as (xxx) xxx-xxxx while typing
const formatPhoneNumber = (text: string) => {
  const cleaned = (text || '').replace(/\D/g, '');
  if (cleaned.length >= 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  } else if (cleaned.length >= 6) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length >= 3) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  }
  return cleaned;
};

export const TreatmentsInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<TreatmentsInputScreenRouteProp>();

  const { treatmentData, isEditing } = route.params || {};

  const [treatmentType, setTreatmentType] = useState(treatmentData?.treatmentType || '');
  const [purpose, setPurpose] = useState(treatmentData?.purpose || '');
  const [facilityName, setFacilityName] = useState(treatmentData?.facilityName || '');
  const [location, setLocation] = useState(treatmentData?.location || '');
  const [phone, setPhone] = useState(formatPhoneNumber(treatmentData?.phone || ''));
  const [insurance, setInsurance] = useState(treatmentData?.insurance || '');
  const [cost, setCost] = useState(treatmentData?.cost || '');
  const [notes, setNotes] = useState(treatmentData?.notes || '');

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!treatmentType.trim()) {
      Alert.alert('Error', 'Please enter the type of treatment');
      return;
    }

    try {
      setSaving(true);

      const treatmentUpdateData = {
        treatmentType: treatmentType.trim(),
        purpose: purpose.trim(),
        facilityName: facilityName.trim(),
        location: location.trim(),
        phone: phone.replace(/\D/g, ''),
        insurance: insurance.trim(),
        cost: cost.trim(),
        notes: notes.trim(),
        taskId: 13, // Current Therapy task ID
      };

      let savedTreatment;

      if (isEditing && treatmentData?.id) {
        // Update existing record
        savedTreatment = await treatmentsService.updateTreatment(
          treatmentData.id,
          treatmentUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedTreatment?.data || savedTreatment;
        logger.debug('Treatment updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection
        navigation.navigate('TreatmentsLockUnlock', { treatmentId: updatedData.id });
      } else {
        // Create new record
        savedTreatment = await treatmentsService.createTreatment(treatmentUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedTreatment?.data || savedTreatment;
        logger.debug('Treatment saved successfully with ID:', createdData.id);

        // Show options for adding another or being done
        Alert.alert(
          'Treatment Saved',
          'Would you like to add another treatment?',
          [
            {
              text: 'Done',
              onPress: () => {
                // Navigate to lock/unlock screen with the saved treatment ID
                navigation.navigate('TreatmentsLockUnlock', { treatmentId: createdData.id });
              },
            },
            {
              text: 'Add Another',
              onPress: () => {
                // Reset form for new entry
                setTreatmentType('');
                setPurpose('');
                setFacilityName('');
                setLocation('');
                setPhone('');
                setInsurance('');
                setCost('');
                setNotes('');
              },
            },
          ]
        );
      }
    } catch (error) {
      logger.error('Error saving treatment:', error);
      Alert.alert('Error', 'Failed to save treatment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          {isEditing ? 'Edit Treatment Info' : 'Add Treatment Info'}
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Type of Treatment or Therapy</Text>
            <TextInput
              style={styles.input}
              value={treatmentType}
              onChangeText={setTreatmentType}
              placeholder="Enter treatment type"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Reason for Treatment</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={purpose}
              onChangeText={setPurpose}
              placeholder="Enter reason for treatment"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Treatment Facility Name</Text>
            <TextInput
              style={styles.input}
              value={facilityName}
              onChangeText={setFacilityName}
              placeholder="Enter facility name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Location of Treatment</Text>
            <TextInput
              style={styles.input}
              value={location}
              onChangeText={setLocation}
              placeholder="Enter treatment location"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Facility Phone Number</Text>
            <TextInput
              style={styles.input}
              value={phone}
              onChangeText={(text) => setPhone(formatPhoneNumber(text))}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Covered by Insurance?</Text>
            <TextInput
              style={styles.input}
              value={insurance}
              onChangeText={setInsurance}
              placeholder="Yes/No or insurance details"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Cost of Treatment</Text>
            <TextInput
              style={styles.input}
              value={cost}
              onChangeText={setCost}
              placeholder="Enter cost (e.g., $200/session)"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Additional Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={notes}
              onChangeText={setNotes}
              placeholder="Enter any additional notes"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.submitButtonContainer}>
            <TouchableOpacity
              style={styles.saveButton}
              onPress={handleSave}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>
                  {isEditing ? 'Update' : 'Submit'}
                </Text>
              )}
            </TouchableOpacity>
          </View>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    minHeight: s(80),
    textAlignVertical: 'top',
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
});